var CalendarBase = require("./CalendarBase");
var Customizing = require("../Customizing");

/**
 * The CalendarDateTime class represents a date/datetime/time data type.
 * It handles the formatting and accepts several options like
 * calendar, a custom format string and a subtype.
 *
 * @param {Object} [oConfig] A configuration object that is used to specify the formatting
 * @param {boolean} [oConfig.displayTimezone=true] Whether the IANA timezone is being displayed
 * @param {string} [oConfig.timezone] Specific timezone that will be applied
 * @param {string} [oConfig.timezoneProperty] Property name that references the timezone
 * @constructor
 */
function CalendarDateTime(oConfig) {
	CalendarBase.call(this, oConfig);

	this.timezone = oConfig ? oConfig.timezone : null;
	this.timezoneProperty = oConfig ? oConfig.timezoneProperty : null;
	this.displayTimezone = oConfig && typeof oConfig.displayTimezone === "boolean" ? oConfig.displayTimezone : true;

	this.styleIds = {};
}

CalendarDateTime.prototype = Object.create(CalendarBase.prototype);
CalendarDateTime.prototype.constructor = CalendarDateTime;

CalendarDateTime._oCache = new Map();

/**
 * Returns the default NumberFormat for the specific type.
 *
 * @param {Object} oData Business object that gets bound to the styling template
 * @returns {string} NumberFormat template for type DateTime
 * @private
 */
CalendarDateTime.prototype.getDefaultFormat = function(oData) {
	var sTemplate, sTimezone;

	sTemplate =	Object.getPrototypeOf(CalendarDateTime.prototype).getDefaultFormat.call(this)
		|| CalendarBase.BUILTIN[22];

	if ((this.calendar === "islamic" && sTemplate === "d mmmm yyyy")
		|| (this.calendar === "japanese" && sTemplate === "ge.m.d")) {
		sTemplate += " hh:mm";
	}

	/* Apply timezone on the cell format only for line item specific timezone */
	if (this.timezoneProperty && this.displayTimezone) {
		sTimezone = Customizing.getTimezoneTranslation(this.getTimezone(oData));
		sTemplate = `_-${sTemplate}* "${sTimezone}"`;
	}

	return sTemplate;
};

/**
 * Returns the IANA timezone string that is used either for the whole
 * column or depending on the row data. The specific timezone is obtained
 * in the following order (properties):
 *
 * 1. timezoneProperty 	(dynamic)
 * 2. timezone			(static)
 * 3. utc				(UTC)
 *
 * @param {Object} oData Business object that represents a row
 * @returns {string|null} Specific IANA timezone string or null if no timezone could be evaluated
 */
CalendarDateTime.prototype.getTimezone = function(oData) {
	if (oData && oData[this.timezoneProperty] && typeof oData[this.timezoneProperty] === 'string') {
		return oData[this.timezoneProperty];
	}

	if (this.timezone) {
		return this.timezone;
	}

	return 'UTC';
};

/**
 * Returns the assigned ID of the Type specific Style that is
 * registered in the Styles class.
 *
 * @param {Object} oData Business Object that contains the property for the UoM
 * @param {string|number} [vValue] Explicit cell value that might be used for semantic formatting
 * @returns {number} ID of the registered Style
 */
CalendarDateTime.prototype.getStyleId = function(oData, vValue) {
	var iStyleId, sTimezone;

	/* Handle simple numeric without unitProperty */
	if (!this.timezoneProperty) {
		return Object.getPrototypeOf(CalendarDateTime.prototype).getStyleId.call(this, oData, vValue);
	}

	if (!oData) {
		return 0;
	}

	sTimezone = this.getTimezone(oData);
	iStyleId = this.styleIds[sTimezone];

	if (typeof iStyleId === "undefined") {
		this.styleIds[sTimezone] = this.styles.addStyle(this._createConfiguration(oData));
	}

	return this.styleIds[sTimezone];
};

/**
 * Converts a JavaScript Date timestamp to an Office Open XML date
 * representation. Office Open XML date epoch starts at 00.01.1900
 * which is 31.12.1899 and is represented as a number of days since
 * beginning of the epoch.
 *
 * The Javascript Date epoch starts at 01.01.1970 and is represented
 * as a number of milliseconds before or after the beginning of the
 * epoch.
 *
 * The amount of days between 01.01.1900 and 01.01.1970 is exactly
 * 25567. According to the wrong offset in Excel (00.01.1900 instead
 * of 01.01.1900) and the known leap year bug in Excel, we need to
 * add two additional days.
 *
 * https://support.microsoft.com/en-us/help/214326/excel-incorrectly-assumes-that-the-year-1900-is-a-leap-year
 *
 * Time specific information is represented as fraction. This means
 * that the value 30.5 represents 30.5 days after the 31.12.1899
 * where.5 stands for half a day. A day is defined as 24 hours, so
 * half a day means 12 hours since the beginning of the day at
 * 00:00:00. If the integer part of the value is greater than 0 date
 * specific information will be present in the cell.
 *
 * This also applies for time formatted cells. Although the cell only
 * displays the time specific part, the editing bar still shows the
 * date specific part. Therefore it is necessary to remove the date
 * specific part from the value in the XLSX file.
 *
 * The input format can contain milliseconds, which cannot be
 * displayed, since no literal is provided for this in the number
 * formatting of the Office Open XML Spreadsheet Format. This causes
 * milliseconds to be rounded to the nearest second, which can result
 * in a difference of one second. Therefore, milliseconds are
 * intentionally cut off during conversion.
 *
 * @param {number} nTimestamp JavaScript Date timestamp
 * @param {string} [oData] Defines if the timestamp remains untouched or if a timezone specific offset is considered
 * @return {number} Amount of days since 31.12.1899
 * @private
 */
CalendarDateTime.prototype.dateToExcel = function(nTimestamp, oData) {
	var iDateDifference, fExcelValue, iTimezoneOffset, oDate, oFormat, oParts, sTimezone;

	sTimezone = this.getTimezone(oData);
	fExcelValue = Object.getPrototypeOf(CalendarDateTime.prototype).dateToExcel.call(this, nTimestamp);

	if (sTimezone && sTimezone !== "UTC") {
		oFormat = CalendarDateTime.getTimezoneFormat(sTimezone);

		/* Calculate offset */
		oDate = new Date(nTimestamp);
		oParts = oFormat.formatToParts(new Date(nTimestamp)).reduce(function(acc, item) {
			switch (item.type) {
				case "day":
				case "hour":
				case "minute":
					acc[item.type] = item.value;
					break;
				default: /* Do nothing */
			}

			return acc;
		}, {});

		iDateDifference = oDate.getUTCDate() - parseInt(oParts.day, 10);

		/*
		 * Once the date difference is more than 1 day,
		 * the day refers to a different month. This
		 * translates into the opposite offset.
		 */
		if (iDateDifference > 1) {
			iDateDifference = -1;
		} else if (iDateDifference < -1) {
			iDateDifference = 1;
		}

		iTimezoneOffset = iDateDifference * 24 * 60;
		iTimezoneOffset += (oDate.getUTCHours() - parseInt(oParts.hour, 10)) * 60;
		iTimezoneOffset += (oDate.getUTCMinutes() - parseInt(oParts.minute, 10));

		/* Apply offset to Excel date time representation */
		fExcelValue -= (iTimezoneOffset / 1440); // 1440 minutes per day
	}

	return fExcelValue;
};

/**
 * Returns a Intl.DateTimeFormat for the given timezone.
 *
 * @param {string} sTimezone Timezone that will be used by the format
 * @returns {Intl.DateTimeFormat} Timezone specific format
 */
CalendarDateTime.getTimezoneFormat = function(sTimezone) {
	var oCachedEntry, oInstance, oOptions;

	oCachedEntry = CalendarDateTime._oCache.get(sTimezone);

	if (oCachedEntry) {
		return oCachedEntry;
	}

	oOptions = {
		hourCycle: "h23",
		hour: "2-digit",
		minute: "2-digit",
		day: "2-digit",
		timeZone: sTimezone,
		era: "narrow"
	};

	oInstance = new Intl.DateTimeFormat("en-US", oOptions);

	CalendarDateTime._oCache.set(sTimezone, oInstance);

	return oInstance;
};

module.exports = CalendarDateTime;